import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, Loader2, Lock, Mail } from 'lucide-react';
import { Button } from '@/theme/components/ui/button';
import { Input } from '@/theme/components/ui/input';
import { Label } from '@/theme/components/ui/label';
import { Checkbox } from '@/theme/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/core/i18n';

export interface LoginFormProps {
  onSubmit: (credentials: { email: string; password: string; rememberMe: boolean }) => void | Promise<void>;
  isSubmitting: boolean;
}

/**
 * LoginForm — credentials form (right column of the split screen).
 *
 * Mirrors the mockup layout: small uppercase kicker with an inline accent
 * line, Fraunces serif headline, sans subtitle, then the email + password
 * fields, "remember me" toggle, and the gradient ocean submit button. The
 * locale chip and the wristband alt sign-in are owned by the parent so this
 * component stays focused on credentials only.
 */
export function LoginForm({ onSubmit, isSubmitting }: LoginFormProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void onSubmit({ email, password, rememberMe });
  };

  return (
    <div className="w-full">
      <div className="mb-8">
        <p className="mb-3 inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.08em] text-ocean-700">
          <span aria-hidden="true" className="h-px w-3.5 bg-ocean-500" />
          {t('auth.login.kicker')}
        </p>
        <h1 className="font-serif text-[34px] font-semibold leading-tight tracking-tight text-foreground">
          {t('auth.login.welcome')}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('auth.login.subtitle')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <div className="space-y-2">
          <Label htmlFor="login-email" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t('auth.login.email.label')}
          </Label>
          <div className="relative">
            <Mail
              className="pointer-events-none absolute left-[14px] top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id="login-email"
              type="email"
              required
              autoComplete="email"
              placeholder={t('auth.login.email.placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              className="h-12 rounded-lg pl-10 focus-visible:ring-[3px] focus-visible:ring-ocean-500/15 focus-visible:ring-offset-0 focus-visible:border-ocean-500"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="login-password" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t('auth.login.password.label')}
          </Label>
          <div className="relative">
            <Lock
              className="pointer-events-none absolute left-[14px] top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="current-password"
              placeholder={t('auth.login.password.placeholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
              className="h-12 rounded-lg pl-10 pr-10 focus-visible:ring-[3px] focus-visible:ring-ocean-500/15 focus-visible:ring-offset-0 focus-visible:border-ocean-500"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              disabled={isSubmitting}
              aria-label={
                showPassword
                  ? t('auth.login.password.hide')
                  : t('auth.login.password.show')
              }
              className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(checked === true)}
              disabled={isSubmitting}
            />
            <span>{t('auth.login.rememberMe')}</span>
          </label>
          <Link
            to="/auth/forgot-password"
            className="text-xs font-medium text-ocean-700 hover:text-ocean-800 hover:underline"
          >
            {t('auth.login.forgotPassword', 'Forgot password?')}
          </Link>
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className={cn(
            'group relative h-12 w-full overflow-hidden rounded-lg text-sm font-semibold',
            'bg-gradient-to-br from-ocean-700 to-ocean-800 text-sand-50',
            'hover:from-ocean-700 hover:to-ocean-900',
            'disabled:opacity-70',
          )}
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-sand-50/30 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[400%]"
          />
          <span className="relative flex items-center justify-center gap-2">
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t('auth.login.submitting')}
              </>
            ) : (
              <>
                {t('auth.login.submit')}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
              </>
            )}
          </span>
        </Button>
      </form>
    </div>
  );
}

export default LoginForm;
